import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PropertyCard } from '../components/PropertyCard';
import { MOCK_PROPERTIES, toPropertyCardFormat } from '../mock/index';
import { ProIcon } from '../components/ProIcon';

// Collection data with SEO-optimized content
const collections = [
  {
    id: 'beachfront',
    title: 'Beachfront Rentals',
    headline: 'Step from Your Rental to the Sand',
    description: 'These homes sit within easy walking distance of the beach — no car, no shuttle, no compromise. Perfect for families who want to live in and out of the sea all day, with a private pool waiting back at the rental.',
    count: 12,
    cta: 'Browse Beachfront',
    icon: 'water',
    image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?q=80&w=1200&h=800&auto=format&fit=crop',
    altText: 'Beachfront villa rental in Marbella with direct beach access and private pool',
  },
  {
    id: 'golfside',
    title: 'Golfside Rentals',
    headline: "Stylish Homes Next to Marbella's Finest Fairways",
    description: 'Minutes from Marbella and Benahavis golf courses, with space to relax properly between rounds. These rentals combine great course access with the comfort and privacy that golfers travelling with family actually need.',
    count: 8,
    cta: 'Browse Golfside',
    icon: 'golf-ball-tee',
    image: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?q=80&w=1200&h=800&auto=format&fit=crop',
    altText: 'Luxury golfside villa rental near Marbella golf courses with family amenities',
  },
  {
    id: 'ultra-luxury',
    title: 'Ultra Luxury Rentals',
    headline: "For Guests Who Won't Compromise",
    description: 'Statement rentals with exceptional design, premium finishes, and total privacy. Every property in this collection has been hand-selected for guests who expect the highest level — in the rental and in the service around it.',
    count: 6,
    cta: 'Browse Ultra Luxury',
    icon: 'crown',
    image: 'https://images.unsplash.com/photo-1613977257363-707ba9348227?q=80&w=1200&h=800&auto=format&fit=crop',
    altText: 'Ultra luxury villa rental in Marbella with premium design and private amenities',
  },
  {
    id: 'family-friendly',
    title: 'Family Friendly Rentals',
    headline: 'Everything a Family Actually Needs',
    description: "Big kitchens. Safe outdoor spaces. Private pools with shallow areas. Multiple bathrooms. Enough beds for everyone. These rentals have been specifically chosen because they work in practice, not just in photos. We've checked them personally.",
    count: 15,
    cta: 'Browse Family Friendly',
    icon: 'users',
    image: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?q=80&w=1200&h=800&auto=format&fit=crop',
    altText: 'Family friendly villa rental in Marbella with safe pool and multiple bedrooms',
  },
];

export const Collections = () => {
  const featuredProperties = MOCK_PROPERTIES.slice(0, 3).map(toPropertyCardFormat);

  return (
    <main className="min-h-screen bg-white pt-24" role="main" aria-label="Rental Collections">
      {/* Hero Section - SEO Optimized */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="ui-eyebrow-brand mb-4">Curated Rental Collections</p>
          <h1 className="ui-page-title mb-6 max-w-4xl mx-auto">
            Find The Right Stay Faster.
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Beachfront, golf, family-ready, or full privacy. Start with the stay style that fits.
          </p>
        </motion.div>

        {/* Collection Cards Grid - UX Optimized with Level 3 Buttons */}
        <section
          className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-10 sm:mb-20"
          aria-label="Rental Collection Categories"
        >
          {collections.map((collection, index) => (
            <motion.article
              key={collection.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="group bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col"
              aria-labelledby={`collection-title-${collection.id}`}
            >
              {/* Image Container with proper aspect ratio */}
              <div className="relative h-44 sm:h-56 overflow-hidden">
                <img
                  src={collection.image}
                  alt={collection.altText}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading={index < 2 ? 'eager' : 'lazy'}
                />
                {/* Collection Badge */}
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm">
                  <ProIcon icon={collection.icon} family="light" className="w-4 h-4 text-brand-ocean" />
                  <span className="text-xs font-bold text-slate-700">{collection.count} Rentals</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 sm:p-8 flex flex-col flex-grow">
                <h2
                  id={`collection-title-${collection.id}`}
                  className="text-xl sm:text-2xl font-black text-brand-ocean tracking-tight mb-2"
                >
                  {collection.title}
                </h2>
                <p className="text-brand-ocean/80 font-semibold text-sm sm:text-base mb-3">
                  {collection.headline}
                </p>
                <p className="text-brand-gray leading-relaxed text-sm sm:text-base mb-5 flex-grow">
                  {collection.description}
                </p>

                {/* CTA Section - Level 3 Tertiary Button */}
                <div className="flex items-center justify-between gap-4 pt-2 border-t border-slate-100">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                    {collection.count} Properties
                  </span>

                  {/* Level 3 Button: Tertiary/Outline Style */}
                  <Link
                    to={`/destinations?collection=${collection.id}`}
                    className="ui-btn-tertiary text-xs sm:text-sm py-2.5 px-4 sm:px-6 gap-2 group/btn"
                    aria-label={`Browse ${collection.title}`}
                  >
                    <span>{collection.cta}</span>
                    <ProIcon
                      icon="arrow-right"
                      family="light"
                      className="w-3.5 h-3.5 transition-transform duration-200 group-hover/btn:translate-x-1"
                    />
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </section>

        {/* Featured Properties Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          aria-labelledby="featured-heading"
        >
          <div className="text-center mb-6 sm:mb-8">
            <h2 id="featured-heading" className="ui-section-title">
              Featured Rentals Across All Collections
            </h2>
            <p className="text-slate-500 mt-2 text-sm sm:text-base">
              A fast look at the homes guests shortlist first
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {featuredProperties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>

          {/* View All CTA - Secondary Button */}
          <div className="text-center mt-8 sm:mt-12">
            <Link
              to="/destinations"
              className="ui-btn-secondary inline-flex items-center gap-2 group"
              aria-label="View all rental properties"
            >
              <span>View All Rentals</span>
              <ProIcon icon="arrow-right" family="light" className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </motion.section>
      </header>
    </main>
  );
};
